import { useState, useCallback } from 'react';

import useViewport from 'Hooks/useViewport';
import pauseYTVideos from 'Helpers/Video/VideoHelpers';

/**
 * Custom hook to control the carousel track position and interactions
 * @param {number} trackSlideWidth - Width of each slide in the carousel
 * @param {number} trackGap - Gap between slides in the carousel
 * @param {number} trackSlidesToShow - Number of slides to show in the carousel
 * @param {number} slidesLength - Total number of slides in the carousel
 * @param {number} sliderWidth - Width of the main slider
 * @returns {Object} Object containing track position, interaction state, and control functions
 */
export default function useCarouselTrackControls({
  trackSlideWidth,
  trackGap,
  trackSlidesToShow,
  slidesLength,
  sliderWidth,
}) {
  const [trackPosition, setTrackPosition] = useState(0);
  const [interacted, setInteracted] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [activeGameNumber, setActiveGameNumber] = useState(0);

  const { isAboveMd } = useViewport();

  // Function to load the next slide in the main carousel
  const loadMainNext = useCallback(() => {
    if (activeGameNumber + 1 === slidesLength) {
      setSliderPosition(0);
      setActiveGameNumber(0);
    } else {
      setSliderPosition(sliderPosition - sliderWidth);
      setActiveGameNumber(activeGameNumber + 1);
    }

    // Move track on desktop
    if (isAboveMd && slidesLength > trackSlidesToShow) {
      if (activeGameNumber + 1 === slidesLength) {
        setTrackPosition(0);
      } else {
        setTrackPosition(-(activeGameNumber + 1) * (trackSlideWidth + trackGap));
      }
    }

    if (!interacted) {
      setInteracted(true);
    }

    // Pause any YouTube videos when clicking next.
    pauseYTVideos();
  }, [
    activeGameNumber,
    sliderPosition,
    sliderWidth,
    slidesLength,
    isAboveMd,
    trackSlidesToShow,
    trackSlideWidth,
    trackGap,
    interacted,
  ]);

  // ====================================================================================================

  // Function to load the previous slide in the main carousel
  const loadMainPrevious = useCallback(() => {
    if (activeGameNumber === 0) {
      setSliderPosition(sliderPosition - (sliderWidth * (slidesLength - 1)));
      setActiveGameNumber(slidesLength - 1);
    } else {
      setSliderPosition(sliderPosition + sliderWidth);
      setActiveGameNumber(activeGameNumber - 1);
    }

    // Move track on desktop
    if (isAboveMd && slidesLength > trackSlidesToShow) {
      if (activeGameNumber === 0) {
        setTrackPosition(-(slidesLength - 1) * (trackSlideWidth + trackGap));
      } else {
        setTrackPosition(-(activeGameNumber - 1) * (trackSlideWidth + trackGap));
      }
    }

    if (!interacted) {
      setInteracted(true);
    }

    // Pause any YouTube videos when clicking previous.
    pauseYTVideos();
  }, [
    activeGameNumber,
    sliderPosition,
    sliderWidth,
    slidesLength,
    isAboveMd,
    trackSlidesToShow,
    trackSlideWidth,
    trackGap,
    interacted,
  ]);

  // ====================================================================================================

  // Function to handle clicking on a track item
  const clickTrackItem = useCallback((number) => {
    // On desktop, we want to move the track to the left so that the clicked item is in view.
    if (isAboveMd && slidesLength > trackSlidesToShow) {
      setTrackPosition(-(number) * (trackSlideWidth + trackGap));
    }
    // When clicking a track item, we want to update the sliderPosition and activeGameNumber to the clicked item's position.
    setSliderPosition(-(sliderWidth * number));
    setActiveGameNumber(number);

    if (!interacted) {
      setInteracted(true);
    }

    // Pause any YouTube videos when clicking a track item.
    pauseYTVideos();
  }, [
    isAboveMd,
    slidesLength,
    trackSlidesToShow,
    trackSlideWidth,
    trackGap,
    sliderWidth,
    setSliderPosition,
    setActiveGameNumber,
    interacted,
  ]);

  // ====================================================================================================

  // Function to load the next set of slides in the carousel
  const loadTrackNext = useCallback(() => {
    // Calculate the total movement of the track based on the number of slides to show and the gap between them.
    const totalMovement = (trackSlideWidth + trackGap) * trackSlidesToShow;

    // The track length is the width of each slide multiplied by the number of slides minus one.
    // We subtract one slide because the track starts at 0. trackLength is negative because the track moves to the left.
    const trackLength = -(trackSlideWidth + trackGap) * (slidesLength - 1);

    // If clicking next would take the track position past the end of the track, we set it to the end.
    // This is to prevent the track from moving beyond the last slide.
    if ((trackPosition - totalMovement) < trackLength) {
      setTrackPosition(trackLength);
    } else {
      setTrackPosition(trackPosition - totalMovement);
    }

    if (!interacted) {
      setInteracted(true);
    }
  }, [trackPosition, trackSlideWidth, trackGap, trackSlidesToShow, slidesLength, interacted]);

  // ====================================================================================================

  // Function to load the previous set of slides in the carousel
  const loadTrackPrevious = useCallback(() => {
    // Calculate the total movement of the track based on the number of slides to show and the gap between them.
    const totalMovement = (trackSlideWidth + trackGap) * trackSlidesToShow;

    // If clicking previous would take the track position above 0, we set it to 0.
    // This is to prevent the track from moving beyond the first slide.
    if ((trackPosition + totalMovement) > 0) {
      setTrackPosition(0);
    } else {
      setTrackPosition(trackPosition + totalMovement);
    }

    if (!interacted) {
      setInteracted(true);
    }
  }, [trackPosition, trackSlideWidth, trackGap, trackSlidesToShow, interacted]);

  // ====================================================================================================

  // Function to reset the main slider position to the start (zero)
  const resetSliderPosition = useCallback(() => {
    setSliderPosition(0);
  }, []);

  // ====================================================================================================

  // Function to reset the active game number to zero
  const resetActiveGameNumber = useCallback(() => {
    setActiveGameNumber(0);
  }, []);

  // ====================================================================================================

  // Function to reset the track position to the start (zero)
  const resetTrackPosition = useCallback(() => {
    setTrackPosition(0);
  }, []);

  return {
    trackPosition,
    interacted,
    loadTrackNext,
    loadTrackPrevious,
    resetTrackPosition,
    resetActiveGameNumber,
    resetSliderPosition,
    setInteracted,
    sliderPosition,
    activeGameNumber,
    clickTrackItem,
    loadMainNext,
    loadMainPrevious,
  };
}
